import { call, ForkEffect, put, select, takeLatest } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { PayloadAction } from '@reduxjs/toolkit';
import { subscriptionActions } from './subscriptionSlice';
import { getProductById, getProducts } from '../../api/subscriptionApi';
import { confirmPaymentAndUnlockApi, createPaymentIntent, getClientSecret, retryPayment } from '../../api/stripeApi';
import { getPaymentByOrganizationId } from '../../api/paymentApi';
import { RootState } from '../store';
import { PaymentProcessingStatus } from '../../utils/enums/payment-processing-status.enum';
import { authActions } from '../auth/authSlice';

function* watchGetProducts() {
  try {
    yield put(subscriptionActions.setComponentStatus({ getProductsLoading: true }));
    const res: AxiosResponse = yield call(getProducts);
    yield put(subscriptionActions.setComponentStatus({ getProductsLoading: false }));
    yield put(subscriptionActions.setProducts(res.data));
  } catch (err) {
    yield put(subscriptionActions.setComponentStatus({ getProductsLoading: false }));
  }
}

function* watchGetProductById(action: PayloadAction<string>) {
  try {
    yield put(subscriptionActions.setComponentStatus({ getProductLoading: true }));
    const res: AxiosResponse = yield call(getProductById, action.payload);
    yield put(subscriptionActions.setComponentStatus({ getProductLoading: false }));
    yield put(subscriptionActions.setProductDetails(res.data));
  } catch (err) {
    yield put(subscriptionActions.setComponentStatus({ getProductLoading: false }));
  }
}

function* fetchSetupIntentSecretSaga(action: PayloadAction<{ customerId: string }>) {
  try {
    const response: AxiosResponse = yield call(getClientSecret, action.payload.customerId);
    if (response.status === 201) {
      yield put(subscriptionActions.setClientSecret(response.data.setupIntentSecret));
    }
  } catch (err) {
    console.error('Error fetching setup intent secret:', err);
  }
}

function* fetchPaymentIntentSecretSaga(action: PayloadAction<{ customerId: string; amount: number }>) {
  try {
    const response: AxiosResponse = yield call(createPaymentIntent, action.payload.customerId, action.payload.amount);
    if (response.status === 201) {
      yield put(subscriptionActions.setClientSecret(response.data.paymentIntentSecret));
    }
  } catch (err) {
    console.error('Error fetching payment intent secret:', err);
  }
}

function* processPaymentSaga() {
  try {
    yield put(subscriptionActions.setPaymentProcessing(PaymentProcessingStatus.LOADING));

    const state: RootState = yield select((rootState: RootState) => rootState);
    const { stripeAccountId, organizationId } = state.auth;

    if (!stripeAccountId) {
      throw new Error('Stripe account ID is missing');
    }

    if (!organizationId) {
      throw new Error('Organization ID is missing');
    }

    const paymentsResponse: AxiosResponse = yield call(getPaymentByOrganizationId, organizationId);

    if (paymentsResponse.status !== 200) {
      throw new Error('Failed to fetch payments data');
    }

    const paymentsData = paymentsResponse.data;

    const overduePayments = paymentsData.filter((payment: any) => payment.paymentStatus === 'overdue');

    if (overduePayments.length === 0) {
      yield put(subscriptionActions.setPaymentProcessing(PaymentProcessingStatus.FAILED));
      yield put(subscriptionActions.setPaymentError('No overdue payments found.'));
      return;
    }

    overduePayments.sort(
      (a: any, b: any) => new Date(b.paidDateAndTime).getTime() - new Date(a.paidDateAndTime).getTime(),
    );

    const latestOverduePayment = overduePayments[0];
    const { amount } = latestOverduePayment;

    if (amount <= 0) {
      yield put(subscriptionActions.setPaymentProcessing(PaymentProcessingStatus.FAILED));
      yield put(subscriptionActions.setPaymentError('Invalid payment amount.'));
      return;
    }

    const response: AxiosResponse = yield call(retryPayment, amount);

    if (response.status === 201 && response.data.status === 'success') {
      yield put(subscriptionActions.setPaymentProcessing(PaymentProcessingStatus.SUCCEEDED));

      const paymentIntentId = response.data.result.id;

      yield put(subscriptionActions.confirmPaymentAndUnlockAsync({ paymentIntentId }));
    } else {
      yield put(subscriptionActions.setPaymentProcessing(PaymentProcessingStatus.FAILED));
      yield put(subscriptionActions.setPaymentError('Payment processing failed'));
    }
  } catch (err) {
    yield put(subscriptionActions.setPaymentProcessing(PaymentProcessingStatus.FAILED));
    yield put(subscriptionActions.setPaymentError('An error occurred while processing the payment'));
    console.error('Error processing payment:', err);
  }
}

function* confirmPaymentAndUnlockSaga(action: PayloadAction<{ paymentIntentId: string }>) {
  try {
    const { paymentIntentId } = action.payload;

    const response: AxiosResponse = yield call(confirmPaymentAndUnlockApi, paymentIntentId);

    if (response.status === 200) {
      yield put(authActions.setLogInStatus(true));
      yield put(subscriptionActions.setPaymentProcessing(PaymentProcessingStatus.SUCCEEDED));
      window.location.href = '/login';
    } else {
      yield put(subscriptionActions.setPaymentProcessing(PaymentProcessingStatus.FAILED));
    }
  } catch (error) {
    yield put(subscriptionActions.setPaymentProcessing(PaymentProcessingStatus.FAILED));
    console.error('Error confirming payment and unlocking account:', error);
  }
}

export function* watchSubscriptionSagas(): Generator<ForkEffect, void> {
  yield takeLatest(subscriptionActions.getProductsAsync, watchGetProducts);
  yield takeLatest(subscriptionActions.getProductByIdAsync, watchGetProductById);
  yield takeLatest(subscriptionActions.fetchSetupIntentSecretAsync, fetchSetupIntentSecretSaga);
  yield takeLatest(subscriptionActions.fetchPaymentIntentSecretAsync, fetchPaymentIntentSecretSaga);
  yield takeLatest(subscriptionActions.processPaymentAsync, processPaymentSaga);
  yield takeLatest(subscriptionActions.confirmPaymentAndUnlockAsync, confirmPaymentAndUnlockSaga);
}
const subscriptionSagas = watchSubscriptionSagas;
export default subscriptionSagas;
